import { useEffect, useRef, useState } from 'react';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer, Group, Title } from '@mantine/core';
import { FormattedMessage } from 'react-intl';

import { FreightForm } from './components/freight-form/FreightForm';
import { RatesPanel } from './components/rates-panel/RatesPanel';
import { freightBrowserMessages } from './freight-browser-messages';
import { reconcileSelection } from '../freight-rate-card';
import styles from './FreightBrowser.module.css';

import type { FreightBrowserProps } from './freight-browser-types';
import type { LtlQuote } from '../../types/ltl';
import type { RefObject } from 'react';

const resolvePortalTarget = (portalRef: RefObject<HTMLElement> | HTMLElement | null | undefined): HTMLElement | undefined => {
  if (portalRef === null || portalRef === undefined) return undefined;
  if ('current' in portalRef) return portalRef.current ?? undefined;
  return portalRef;
};

export const FreightBrowser = ({
  isOpen,
  onClose,
  onBrowseParcelRates,
  onAddShipFrom,
  isHazmatEnabled,
  formState,
  onFormChange,
  shipFromOptions,
  shipFromAddress,
  destinationAddress,
  rates,
  isLoadingRates,
  rateError,
  rateErrorCode,
  connectedFreightProviderId,
  sellerFreightProviderId,
  displayWeightUnit,
  displayDimensionUnit,
  onBrowseRates,
  onSaveQuote,
  onConfigureShipment,
  onDensityAction,
  portalRef,
  zIndex,
}: FreightBrowserProps) => {
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined);
  const portalTarget = resolvePortalTarget(portalRef);

  const selectedQuote = rates.find((rate) => rate.offerId === selectedOfferId) ?? null;

  const prevRatesRef = useRef<LtlQuote[]>([]);
  const prevSelectedQuoteRef = useRef<LtlQuote | null>(null);
  useEffect(() => {
    const prevRates = prevRatesRef.current;
    prevRatesRef.current = rates;
    if (rates.length === 0) {
      prevSelectedQuoteRef.current = prevRates.find((r) => r.offerId === selectedOfferId) ?? null;
      setSelectedOfferId(undefined);
    } else {
      const reconciled = reconcileSelection(prevSelectedQuoteRef.current, rates, formState.insurance.isInsured);
      if (reconciled) {
        setSelectedOfferId(reconciled.offerId);
        prevSelectedQuoteRef.current = null;
      }
    }
  }, [rates]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectQuote = (quote: LtlQuote) => {
    prevSelectedQuoteRef.current = null;
    setSelectedOfferId(quote.offerId);
  };

  return (
    <Drawer
      classNames={{ body: styles.drawerBodySlot, content: styles.drawerContent, header: styles.header, title: styles.titleSlot }}
      closeOnClickOutside
      data-ignore-root-focus
      onClose={onClose}
      opened={isOpen}
      padding={0}
      portalProps={portalTarget ? { target: portalTarget } : undefined}
      zIndex={zIndex}
      position='right'
      size='100%'
      title={
        onBrowseParcelRates ? (
          <Group align='center' justify='space-between'>
            <Title order={3} component='span'>
              <FormattedMessage {...freightBrowserMessages.title} />
            </Title>
            <Button onClick={onBrowseParcelRates} variant='hyperlink'>
              <FormattedMessage {...freightBrowserMessages.browseParcelRates} />
            </Button>
          </Group>
        ) : (
          <Title order={3} component='span'>
            <FormattedMessage {...freightBrowserMessages.title} />
          </Title>
        )
      }
    >
      <div className={styles.drawerBody}>
        <div className={styles.formColumn}>
          <FreightForm
            displayDimensionUnit={displayDimensionUnit}
            displayWeightUnit={displayWeightUnit}
            formState={formState}
            isHazmatEnabled={isHazmatEnabled}
            onAddShipFrom={onAddShipFrom}
            onDensityAction={onDensityAction}
            onFormChange={onFormChange}
            sellerFreightProviderId={sellerFreightProviderId}
            shipFromOptions={shipFromOptions}
          />
        </div>
        <div className={styles.ratesColumn}>
          <RatesPanel
            connectedFreightProviderId={connectedFreightProviderId}
            destinationAddress={destinationAddress}
            isInsured={formState.insurance.isInsured}
            isLoading={isLoadingRates}
            onSelectQuote={handleSelectQuote}
            rateError={rateError}
            rateErrorCode={rateErrorCode}
            rates={rates}
            selectedOfferId={selectedOfferId}
            shipFromAddress={shipFromAddress}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.footerFormSection}>
          <Button onClick={onBrowseRates}>
            <FormattedMessage {...freightBrowserMessages.browseRates} />
          </Button>
        </div>
        <div className={styles.footerRatesSection}>
          <Button disabled={rates.length === 0} onClick={() => onSaveQuote(selectedQuote)} variant='tertiary'>
            <FormattedMessage {...freightBrowserMessages.saveQuote} />
          </Button>
          <Button
            disabled={!selectedQuote}
            onClick={() => selectedQuote && onConfigureShipment(selectedQuote)}
            rightSection={<FontAwesomeIcon icon={faArrowRight} />}
            variant='secondary'
          >
            <FormattedMessage {...freightBrowserMessages.configureShipment} />
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
